import { Component, input } from '@angular/core';
import { DatePipe } from '@angular/common';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TableRowData } from '@app/shared/models/table-data-types';
import { ColumnConfig } from '@app/shared/services/view-configs/column-creator';
import { getTimeStatusColor } from '@app/shared/date-time-utils';

@Component({
    selector: 'app-date-display',
    template: `
        <span [matTooltip]="tooltipText">
            {{ displayDatePart }}
            @if (displayTimePart) {
                <span [class]="textColorClass"> {{ displayTimePart }}</span>
            }
        </span>
    `,
    standalone: true,
    imports: [MatTooltipModule],
})
export class DateDisplayComponent {
    value = input<string | Date>('');
    row = input<TableRowData>();
    column = input<ColumnConfig>();
    index = input<number>(0);
    parentRow = input<TableRowData>(); // for expanded rows
    format = input<string>('dd.MM.yyyy');
    showTimeConditionally = input<boolean>(false); // If true, shows time based on row.showDateAnnouncedTime flag
    status = input<string | null>(); // Delivery note or bundle status for color-coding

    private datePipe = new DatePipe('de-DE');

    get displayDatePart(): string {
        if (!this.value()) return '';

        // Always show just the date part (dd.MM.yyyy)
        return (
            this.datePipe.transform(
                this.value(),
                'dd.MM.yyyy',
                'Europe/Berlin'
            ) || ''
        );
    }

    get displayTimePart(): string {
        if (!this.value()) return '';

        // Only show time if showTimeConditionally is true AND showDateAnnouncedTime flag is true on the row data
        if (
            this.showTimeConditionally() &&
            (this.row() as any)?.showDateAnnouncedTime === true
        ) {
            return (
                this.datePipe.transform(
                    this.value(),
                    'HH:mm',
                    'Europe/Berlin'
                ) || ''
            );
        }

        return '';
    }

    get tooltipText(): string {
        if (!this.value()) return '';
        const date = new Date(this.value());
        return `Vollständiges Datum: ${this.datePipe.transform(date, 'dd.MM.yyyy HH:mm:ss', 'Europe/Berlin')}`;
    }

    get textColorClass(): string {
        const value = this.value();
        if (!value) return '';
        const timestamp =
            typeof value === 'number' ? value : new Date(value).getTime();
        const showTime =
            this.showTimeConditionally() &&
            (this.row() as any)?.showDateAnnouncedTime === true;
        return getTimeStatusColor(timestamp, showTime, this.status());
    }
}
